import type {
  DocumentType,
  RiskAssessment,
  RiskAssessmentFilters,
  RiskAssessmentSortColumn,
} from '@nestjs-boilerplate/types';
import { Link, useNavigate } from '@tanstack/react-router';
import type { OnChangeFn, RowSelectionState, SortingState } from '@tanstack/react-table';
import { useAtom, useSetAtom } from 'jotai';
import { AlertCircle, Loader2, MoreHorizontal } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataCardList } from '@/components/ui/data-card-list';
import { DataTable } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MobileSortControl, type MobileSortOption } from '@/components/ui/mobile-sort-control';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useStartAssessment } from '@/features/risk-assessment/api/risk-assessment-wizard';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useFeatureFlag } from '@/lib/use-feature-flag';
import { dashboardViewStateAtom } from '@/stores/dashboard.store';
import { addGeneratingIdAtom } from '@/stores/generating.store';

import {
  useBulkDeleteRiskAssessments,
  useBulkRefreshDocuments,
  useDeleteRiskAssessment,
  useGenerateDocumentLink,
  useRefreshReport,
  useRiskAssessments,
} from '../api/risk-assessments';
import { getColumns } from './columns';
import type { DateFilterValue } from './date-filter';
import { DeleteModal } from './delete-modal';
import { DocumentsModal } from './documents-modal';
import { EmptyState } from './empty-state';
import { FilterBar, type FilterState } from './filter-bar';
import { GenerateLinkModal } from './generate-link-modal';
import { InProgressTable } from './in-progress-table';
import { Pagination } from './pagination';
import type { ReportWindowOption } from './report-window-filter';
import { RiskLevelBadge } from './risk-level-badge';
import { SelectionHeader } from './selection-header';

function formatDate(date: Date | null): string {
  if (!date) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(date));
}

const MOBILE_SORT_OPTIONS: MobileSortOption[] = [
  { id: 'clientRef', label: 'Client Ref', descLabel: 'Z → A', ascLabel: 'A → Z' },
  { id: 'riskLevel', label: 'Risk Level', descLabel: 'Z → A', ascLabel: 'A → Z' },
  {
    id: 'dateReportGenerated',
    label: 'Date Report Generated',
    descLabel: 'Newest first',
    ascLabel: 'Oldest first',
  },
];

// Helper to convert report window options to backend number
function getReportWindowNumber(options: ReportWindowOption[]): number | undefined {
  if (options.length === 0) return undefined;
  // Map frontend values to backend numbers
  // Backend expects: -1 (expired), 3 (1-3 days), 7 (4-7 days), 8 (8+ days)
  const mapping: Record<ReportWindowOption, number> = {
    expired: -1,
    '1-3': 3,
    '4-7': 7,
    '8+': 8,
  };
  // If multiple selected, take the first one (backend only supports single value)
  return mapping[options[0]];
}

// Helper to convert date filter to dateFrom/dateTo
function getDateRange(dateFilter: DateFilterValue): { dateFrom?: Date; dateTo?: Date } {
  // Handle custom date range
  if (dateFilter.preset === 'custom') {
    return { dateFrom: dateFilter.customFrom, dateTo: dateFilter.customTo };
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (dateFilter.preset) {
    case 'today':
      return { dateFrom: today };
    case 'this_week': {
      const dayOfWeek = today.getDay();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - dayOfWeek);
      return { dateFrom: startOfWeek };
    }
    case 'this_month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return { dateFrom: startOfMonth };
    }
    case 'last_month': {
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return { dateFrom: startOfLastMonth, dateTo: endOfLastMonth };
    }
    default:
      return {};
  }
}

export function DashboardContent() {
  const isMobile = useIsMobile();
  const { enabled: documentUploadEnabled } = useFeatureFlag('document_upload');

  // View state (filters, pagination, sorting) is held in a jotai atom so it
  // survives navigation to the report page and back without resetting.
  const [viewState, setViewState] = useAtom(dashboardViewStateAtom);
  const { filters, page, pageSize, sorting } = viewState;

  const setFilters = useCallback(
    (next: FilterState | ((prev: FilterState) => FilterState)) => {
      setViewState((prev) => ({
        ...prev,
        filters: typeof next === 'function' ? next(prev.filters) : next,
      }));
    },
    [setViewState],
  );

  const setPage = useCallback(
    (next: number) => setViewState((prev) => ({ ...prev, page: next })),
    [setViewState],
  );

  const setPageSize = useCallback(
    (next: number) => setViewState((prev) => ({ ...prev, pageSize: next })),
    [setViewState],
  );

  const setSorting: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      setViewState((prev) => ({
        ...prev,
        sorting:
          typeof updaterOrValue === 'function' ? updaterOrValue(prev.sorting) : updaterOrValue,
      }));
    },
    [setViewState],
  );

  // Convert frontend filters to API filters
  const apiFilters: RiskAssessmentFilters = useMemo(() => {
    const dateRange = getDateRange(filters.dateFilter);
    // Extract sorting params from TanStack Table sorting state
    // Cast to RiskAssessmentSortColumn - column IDs match allowed sort columns
    const sortBy = sorting[0]?.id as RiskAssessmentSortColumn | undefined;
    const sortOrder = sorting[0]?.desc ? 'desc' : 'asc';
    return {
      search: filters.search || undefined,
      riskLevels: filters.riskLevels.length > 0 ? filters.riskLevels : undefined,
      reportDownloadWindow: getReportWindowNumber(filters.reportWindow),
      ...dateRange,
      page,
      pageSize,
      status: 'COMPLETED', // Main table shows completed assessments
      sortBy,
      sortOrder: sortBy ? sortOrder : undefined, // Only include sortOrder if sortBy is set
    };
  }, [filters, page, pageSize, sorting]);

  // In-progress filters
  const inProgressFilters: RiskAssessmentFilters = useMemo(
    () => ({
      status: 'IN_PROGRESS',
      page: 1,
      pageSize: 100, // Show all in-progress
    }),
    [],
  );

  // API hooks
  const { data: assessmentsData, isLoading, isError } = useRiskAssessments(apiFilters);
  const { data: inProgressData } = useRiskAssessments(inProgressFilters);

  const deleteAssessmentMutation = useDeleteRiskAssessment();
  const bulkDeleteMutation = useBulkDeleteRiskAssessments();
  const bulkRefreshMutation = useBulkRefreshDocuments();
  const refreshReportMutation = useRefreshReport();
  const generateLinkMutation = useGenerateDocumentLink();
  const startAssessmentMutation = useStartAssessment();
  const navigate = useNavigate();

  // Extract data from API response
  const assessments = assessmentsData?.data ?? [];
  const total = assessmentsData?.total ?? 0;
  const totalPages = assessmentsData?.totalPages ?? 0;
  const inProgressAssessments = inProgressData?.data ?? [];
  const addGeneratingId = useSetAtom(addGeneratingIdAtom);

  // Sync assessments that are currently processing to the generating store
  // This ensures the spinner persists across page refreshes
  useEffect(() => {
    for (const assessment of inProgressAssessments) {
      // Add to generating store if actively processing adverse media
      if (
        assessment.screeningJobStatus === 'FAST_CHECKS_DONE' ||
        assessment.screeningJobStatus === 'PROCESSING'
      ) {
        addGeneratingId(assessment.id);
      }
    }
  }, [inProgressAssessments, addGeneratingId]);

  // Row selection state
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback((updaterOrValue) => {
    setRowSelection((prev) =>
      typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue,
    );
  }, []);

  // Modal states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [generateLinkModalOpen, setGenerateLinkModalOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<RiskAssessment | null>(null);
  const [assessmentsToDelete, setAssessmentsToDelete] = useState<RiskAssessment[]>([]);

  // Reset page when filters change
  const handleFiltersChange = useCallback(
    (newFilters: FilterState) => {
      setFilters(newFilters);
      setPage(1); // Reset to first page when filters change
    },
    [setFilters, setPage],
  );

  // Handle sorting changes - reset to page 1
  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      setSorting(updaterOrValue);
      setPage(1); // Reset to first page when sorting changes
    },
    [setSorting, setPage],
  );

  // Get selected assessment IDs
  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
  const selectedAssessments = assessments.filter((a) => selectedIds.includes(a.id));

  // Column action handlers
  const handleViewReport = (assessment: RiskAssessment) => {
    navigate({ to: '/risk-assessment/$id/report', params: { id: assessment.id } });
  };

  const handleGenerateLink = (assessment: RiskAssessment) => {
    setSelectedAssessment(assessment);
    setGenerateLinkModalOpen(true);
  };

  const handleDelete = (assessment: RiskAssessment) => {
    setAssessmentsToDelete([assessment]);
    setDeleteModalOpen(true);
  };

  const handleViewDetails = (assessment: RiskAssessment) => {
    setSelectedAssessment(assessment);
    setDocumentsModalOpen(true);
  };

  const handleRefresh = (assessment: RiskAssessment) => {
    refreshReportMutation.mutate(assessment.id);
  };

  const handleRerunAssessment = (assessment: RiskAssessment) => {
    // Written before the mutation, not in its onSuccess. `useStartAssessment`
    // navigates from its own hook-level onSuccess, which TanStack Query awaits
    // *before* the per-call one — so a write in there lands after the client
    // details form has already mounted and read the key, and the form only
    // reads it once. Worse, the per-call callback is skipped altogether when
    // the navigate unmounts this component (it is gated on hasListeners), so
    // the ID could be left behind to pollute the next unrelated assessment —
    // the very thing moving it there was meant to prevent.
    localStorage.setItem('okyc_rerun_source_id', assessment.id);
    startAssessmentMutation.mutate(undefined, {
      // Safe where onSuccess was not: the failure path does not navigate, so
      // this component is still mounted and the callback actually fires.
      onError: () => localStorage.removeItem('okyc_rerun_source_id'),
    });
  };

  // Batch action handlers
  const handleBatchRefresh = () => {
    if (selectedIds.length > 0) {
      bulkRefreshMutation.mutate(selectedIds, {
        onSuccess: () => {
          setRowSelection({});
        },
      });
    }
  };

  const handleBatchDelete = () => {
    setAssessmentsToDelete(selectedAssessments);
    setDeleteModalOpen(true);
  };

  const handleDeselectAll = () => {
    setRowSelection({});
  };

  // Delete confirmation handler
  const handleConfirmDelete = () => {
    const idsToDelete = assessmentsToDelete.map((a) => a.id);

    if (idsToDelete.length === 1) {
      deleteAssessmentMutation.mutate(idsToDelete[0], {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setAssessmentsToDelete([]);
          setRowSelection({});
        },
      });
    } else {
      bulkDeleteMutation.mutate(idsToDelete, {
        onSuccess: () => {
          setDeleteModalOpen(false);
          setAssessmentsToDelete([]);
          setRowSelection({});
        },
      });
    }
  };

  // Generate link handler
  const handleSubmitGenerateLink = (
    email: string,
    documentTypes: DocumentType[],
    otherDescription?: string,
  ) => {
    if (selectedAssessment) {
      generateLinkMutation.mutate(
        {
          id: selectedAssessment.id,
          data: {
            assessmentId: selectedAssessment.id,
            clientEmail: email,
            documentTypes,
            otherDescription,
          },
        },
        {
          onSuccess: () => {
            setGenerateLinkModalOpen(false);
            setSelectedAssessment(null);
          },
        },
      );
    }
  };

  // In-progress handlers
  const handleContinueAssessment = (assessment: RiskAssessment) => {
    // If there's a saved payment flow step, go to list-selection to show the modal
    // Also go to list-selection if screening has started (to resume the flow)
    const hasPaymentFlowStep = !!assessment.paymentFlowStep;
    const screeningStarted =
      assessment.screeningJobStatus === 'FAST_CHECKS_DONE' ||
      assessment.screeningJobStatus === 'PROCESSING' ||
      assessment.screeningJobStatus === 'COMPLETED';

    if (hasPaymentFlowStep || screeningStarted) {
      navigate({ to: '/risk-assessment/$id/list-selection', params: { id: assessment.id } });
    } else {
      navigate({ to: '/risk-assessment/$id/client-details', params: { id: assessment.id } });
    }
  };

  const handleDeleteInProgress = (assessment: RiskAssessment) => {
    setAssessmentsToDelete([assessment]);
    setDeleteModalOpen(true);
  };

  const columns = getColumns({
    onViewReport: handleViewReport,
    onGenerateLink: handleGenerateLink,
    onDelete: handleDelete,
    onViewDetails: handleViewDetails,
    onRefresh: handleRefresh,
    onRerunAssessment: handleRerunAssessment,
    documentUploadEnabled,
  });

  // Row class name function for highlighting assessments that need refresh
  // (or visually subduing purged assessments — those are kept only as
  // compliance audit stubs and have no actionable state left).
  const getRowClassName = useCallback((row: RiskAssessment) => {
    if (row.purgedAt) {
      return 'bg-gray-50 text-gray-500';
    }
    if (row.needsRefresh) {
      return 'bg-amber-50 border-l-4 border-l-amber-400';
    }
    return undefined;
  }, []);

  const isEmpty = assessments.length === 0 && inProgressAssessments.length === 0 && !isLoading;
  const hasInProgress = inProgressAssessments.length > 0;

  // Header actions (buttons) - shown with the first header
  const headerActions = (
    <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
      <Link
        to="/deleted-assessments"
        className="text-center text-[14px] text-[#051923] underline hover:no-underline sm:text-left"
      >
        Deleted Assessments
      </Link>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            className="h-[45px] w-full rounded-[5px] bg-[#131313] px-6 text-[16px] font-medium text-white hover:bg-[#131313]/90 sm:w-auto"
            onClick={() => startAssessmentMutation.mutate()}
            disabled={startAssessmentMutation.isPending}
          >
            {startAssessmentMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              'New AML Risk Assessment'
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>Begin a new risk assessment for a client</TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* In Progress Assessments - shown first with buttons if present */}
      <InProgressTable
        assessments={inProgressAssessments}
        onContinue={handleContinueAssessment}
        onDelete={handleDeleteInProgress}
        headerActions={headerActions}
      />

      {/* Header - buttons shown here only if no in-progress assessments */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-[20px] font-semibold text-[#051923]">Client Risk Assessments</h2>
        {!hasInProgress && headerActions}
      </div>

      {/* Filter Bar */}
      <FilterBar filters={filters} onFiltersChange={handleFiltersChange} totalResults={total} />

      {/* Selection Header */}
      <SelectionHeader
        selectedCount={selectedIds.length}
        onRefresh={handleBatchRefresh}
        onDelete={handleBatchDelete}
        onDeselectAll={handleDeselectAll}
      />

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading assessments...</div>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="flex items-center justify-center py-12">
          <div className="text-destructive">Failed to load assessments. Please try again.</div>
        </div>
      )}

      {/* Main Table / Card list or Empty State */}
      {!isLoading && !isError && isEmpty ? (
        <EmptyState />
      ) : !isLoading && !isError ? (
        <>
          {isMobile ? (
            <>
              <MobileSortControl
                options={MOBILE_SORT_OPTIONS}
                value={sorting[0] ? { id: sorting[0].id, desc: sorting[0].desc } : undefined}
                onChange={(next) => {
                  handleSortingChange([{ id: next.id, desc: next.desc }]);
                }}
              />
              <DataCardList<RiskAssessment>
                data={assessments}
                getRowId={(row) => row.id}
                cardConfig={{
                  header: (row) => (
                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[#051923]">{row.optimalKycRef}</span>
                        {row.purgedAt && (
                          <span
                            className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600"
                            title="Personal data and report content were removed after the retention window. The audit reference, risk outcome, and which checks ran are retained for compliance."
                          >
                            Redacted
                          </span>
                        )}
                        {!row.purgedAt && row.needsRefresh && (
                          <AlertCircle className="h-4 w-4 text-amber-500" />
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {row.clientName || (row.purgedAt ? '—' : '')}
                      </span>
                    </div>
                  ),
                  // Redacted rows have no PII or report data left — the
                  // whole menu is a no-op so we hide it. Expired rows still
                  // have the underlying data but access is blocked until
                  // the user refreshes; the menu stays so they can take
                  // other actions, but View Report itself is disabled
                  // below.
                  trailing: (row) =>
                    row.purgedAt ? null : (
                      <div className="flex items-center gap-2" data-no-row-click>
                        <Checkbox
                          checked={!!rowSelection[row.id]}
                          onCheckedChange={(checked) => {
                            setRowSelection((prev) => ({ ...prev, [row.id]: !!checked }));
                          }}
                          aria-label="Select row"
                        />
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              disabled={row.reportExpired}
                              onClick={() => !row.reportExpired && handleViewReport(row)}
                              title={
                                row.reportExpired
                                  ? 'Report download window has expired. Refresh to re-enable.'
                                  : undefined
                              }
                            >
                              View Report
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleRerunAssessment(row)}>
                              Re-run Assessment
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={!documentUploadEnabled}
                              onClick={() => documentUploadEnabled && handleGenerateLink(row)}
                            >
                              Generate Secure Document Request Link
                              {!documentUploadEnabled && (
                                <span className="ml-2 text-[10px] text-gray-400">
                                  (Coming Soon)
                                </span>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(row)}>
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ),
                  fields: [
                    { label: 'Client Ref', render: (row) => row.clientRef },
                    {
                      label: 'Risk Level',
                      render: (row) => <RiskLevelBadge level={row.riskLevel} />,
                    },
                    {
                      label: 'Document Request',
                      visible: () => documentUploadEnabled,
                      render: (row) => {
                        const hasUploads = row.documentRequests && row.documentRequests.length > 0;
                        if (!hasUploads) {
                          return <span className="text-[#051923]">No</span>;
                        }
                        return (
                          <span data-no-row-click className="inline-flex items-center gap-2">
                            <span className="text-[#051923]">Yes</span>
                            <button
                              type="button"
                              onClick={() => handleViewDetails(row)}
                              className="text-[#051923] underline hover:no-underline"
                            >
                              View Details
                            </button>
                          </span>
                        );
                      },
                    },
                    {
                      label: 'Report Window (Days)',
                      render: (row) => {
                        if (row.purgedAt) {
                          return (
                            <span
                              className="text-xs text-gray-500"
                              title="Report content was removed after the retention window"
                            >
                              Redacted
                            </span>
                          );
                        }
                        if (row.reportExpired) {
                          // Refresh disabled once the user has used both
                          // refreshes or the report passed the 30-day hard
                          // cap from generation. `canRefresh` is computed
                          // server-side in RiskAssessmentsService — keep
                          // the gate consistent across UI + API.
                          return (
                            <span data-no-row-click className="inline-flex items-center gap-2">
                              <span className="text-[#051923]">Expired</span>
                              {row.canRefresh ? (
                                <button
                                  type="button"
                                  onClick={() => handleRefresh(row)}
                                  className="text-[#051923] underline hover:no-underline"
                                  title={`${row.refreshesRemaining} of ${row.refreshesUsed + row.refreshesRemaining} refreshes left`}
                                >
                                  Refresh
                                </button>
                              ) : (
                                <span
                                  className="text-xs text-gray-500"
                                  title="Report refresh limit reached"
                                >
                                  No refreshes left
                                </span>
                              )}
                            </span>
                          );
                        }
                        return <span>{row.reportDownloadWindowDays ?? '-'}</span>;
                      },
                    },
                    {
                      label: 'Date Generated',
                      render: (row) => formatDate(row.dateReportGenerated),
                    },
                  ],
                  // Redacted rows have no report data; expired rows have
                  // it but access is gated until the user refreshes. Block
                  // the row click in either case so we don't land on an
                  // empty / forbidden report page.
                  onClick: (row) => {
                    if (row.purgedAt || row.reportExpired) return;
                    handleViewReport(row);
                  },
                  getState: (row) => ({
                    selected: !!rowSelection[row.id],
                    highlighted: !row.purgedAt && row.needsRefresh,
                  }),
                }}
              />
            </>
          ) : (
            <DataTable
              columns={columns}
              data={assessments}
              // Predicate form so 'Select All' skips redacted (purged)
              // rows. They have no PII, no report, no documents — nothing
              // batch-actionable. Mirrors the per-row checkbox disabled
              // state in columns.tsx.
              enableRowSelection={(row) => !row.original.purgedAt}
              rowSelection={rowSelection}
              onRowSelectionChange={handleRowSelectionChange}
              getRowId={(row) => row.id}
              getRowClassName={getRowClassName}
              sorting={sorting}
              onSortingChange={handleSortingChange}
            />
          )}

          {/* Pagination */}
          {total > 0 && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              selectedCount={selectedIds.length}
            />
          )}
        </>
      ) : null}

      {/* Modals */}
      <DeleteModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        itemCount={assessmentsToDelete.length}
      />

      <DocumentsModal
        open={documentsModalOpen}
        onOpenChange={setDocumentsModalOpen}
        assessment={selectedAssessment}
      />

      <GenerateLinkModal
        open={generateLinkModalOpen}
        onOpenChange={setGenerateLinkModalOpen}
        assessment={selectedAssessment}
        onSubmit={handleSubmitGenerateLink}
      />
    </div>
  );
}
